<template>
  <div>
    <Input
      v-if="customField.type === 'input' || customField.type === 'textarea'"
      v-model="value"
      :id="customField._id"
      :label="showLabel ? customField.name : undefined"
      :placeholder="$t('components.client-filter-slide-over.enter-value')"
      :disabled="disabled"
      :required="customField.required"
    />
    <Select
      v-else-if="customField.type === 'selection'"
      v-model="value"
      :options="customField.options!"
      :label="showLabel ? customField.name : undefined"
      :placeholder="
        $t(
          'components.client-detail-view.custom-field-multi-selection-placeholder'
        )
      "
      :disabled="disabled"
      :required="customField.required"
      string
    />
    <TagsInput
      v-else-if="customField.type === CustomFieldType.multiSelection"
      v-model="value"
      :options="customField.options!"
      :label="showLabel ? customField.name : undefined"
      :placeholder="
        customField.options?.length
          ? $t(
            'components.client-detail-view.custom-field-multi-selection-placeholder'
          )
          : $t(
            'components.client-detail-view.custom-field-multi-selection-placeholder-variable-options'
          )
      "
      :disabled="disabled"
      :required="customField.required"
      string
    />
    <Hint v-if="!isValid" class="text-red-600">
      {{ $t('error-messages.invalid-value') }}
    </Hint>
  </div>
</template>

<script lang="ts" setup>
import { type CustomField, CustomFieldType } from '~/types';
import {
  getFallbackValue,
  sanitizeCustomFieldValue,
} from '~/util/custom-field-utils';

const props = defineProps({
  modelValue: {
    type: [String, Array as PropType<string[]>],
  },
  customField: {
    type: Object as PropType<CustomField | { _id: CustomField['_id'] }>,
    required: true,
  },
  showLabel: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const value = computed({
  get: () =>
    sanitizeCustomFieldValue(
      props.customField,
      props.modelValue ?? getFallbackValue(props.customField)
    ),
  set: (newValue) => emit('update:modelValue', newValue),
});

const isValid = computed(() => {
  // Validate value(s) against regex if configured
  if (props.customField.regex && value.value.length) {
    try {
      const regex = new RegExp(props.customField.regex);
      if (Array.isArray(value.value)) {
        if (!value.value.every((str) => regex.test(str))) {
          return false;
        }
      } else if (!regex.test(value.value)) {
        return false;
      }
    } catch {
      return false;
    }
  }

  return true;
});
</script>
